import { AppDispatch } from "@Reducers/index";
import axiosWrapper, { postTo } from "../services/axiosWrapper";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type {
  AddProjectRoleRequest,
  AddProjectRoleResponse,
  DeleteProjectRoleRequest,
  DeleteProjectRoleResponse,
  ChangeProjectRolePermissionRequest,
  ChangeProjectRolePermissionResponse,
  GetProjectRolesRequest,
  ChangeUserRoleFromProjectRequest,
  RemoveUsersFromSingleProjectRequest,
  RemoveUsersFromSingleProjectResponse,
} from "@ServerTypes/projects/project.schema";
import type {
  SaveDefaultSpaceRequest,
  SaveDefaultSpaceResponse,
  RemoveSpaceRequest,
} from "@ServerTypes/spaces/space.schema";
import type {
  ProjectTemplateConfigHandlerRequest,
  ProjectTemplateConfigHandlerResponse,
} from "@ServerTypes/forms/forms.schema";
import {
  setProjectRolesList,
  deletedSpaceStatus,
  getProjectSettingObjectAction as getProjectSettingObject,
} from "../reducers/projectSetting";
import type { Exact } from "@Helpers/types/exact";
import * as Sentry from "@sentry/react";

export async function addProjectRole<T extends Exact<AddProjectRoleRequest, T>>(
  data: T,
): Promise<AddProjectRoleResponse> {
  const response = await axiosWrapper.api.post("/projects/addProjectRole", data);
  return response.data;
}

export async function deleteProjectRole<T extends Exact<DeleteProjectRoleRequest, T>>(
  data: T,
): Promise<DeleteProjectRoleResponse> {
  const response = await axiosWrapper.api.post("/projects/deleteProjectRole", data);
  return response.data;
}

export async function updateProjectRolePermission<T extends Exact<ChangeProjectRolePermissionRequest, T>>(
  data: T,
): Promise<ChangeProjectRolePermissionResponse> {
  const response = await axiosWrapper.api.post("/projects/changeProjectRolePermission", data);
  // Returns the BODY, like every sibling here. Handing back the whole AxiosResponse made
  // `res.status` the HTTP status — always 200, so a `{ status: false }` refusal read as success.
  return response.data;
}

/** `projectId` becomes `GetProjectRolesRequest["id"]`, an `objectIdString` in projects.schema.ts. */
export function getProjectRoles(data: { projectId?: ObjectIdString }) {
  return (dispatch: AppDispatch) => {
    let projectId = data.projectId;
    if (!projectId) {
      // Fallback entry point: the second-to-last URL path segment is the Project `_id` on the
      // project-settings routes. Path segments are outside the type system, so parse rather than
      // tag — the backend rejects a non 24-hex id anyway, so there is nothing to fetch without one.
      const pathArray = location.pathname.split("/");
      projectId = objectIdString.safeParse(pathArray[pathArray.length - 2]).data;
      if (!projectId) return;
    }
    const requestData: GetProjectRolesRequest = { id: projectId };
    // Fire-and-forget thunk: the store is updated from the callback, and the thunk returns void.
    void axiosWrapper.api.post("/projects/getProjectRoles", requestData).then((response) => {
      if (response.data.status) {
        dispatch(setProjectRolesList(response.data.result));
      }
    });
  };
}

export async function saveProjectRole<T extends Exact<ChangeUserRoleFromProjectRequest, T>>(data: T) {
  const res = await axiosWrapper.api.post("/projects/changeUserRoleFromProject", data);
  return res;
}
// get list of user which are added into project

// export function deleteSpace(data) {
//   return async (dispatch: AppDispatch) => {
//     const response = await axiosWrapper.post(
//       `${getBackendUrl()}/spaces/deleteSpacesData`,
//       data
//     );

// delete space from project
/** `spaceId` becomes `RemoveSpaceRequest["key"]["spaceId"]`, an `objectIdString` in spaces.schema.ts. */
export function deleteSpace(data: { spaceId: ObjectIdString }) {
  return (dispatch: AppDispatch) => {
    const requestData: RemoveSpaceRequest = {
      key: { spaceId: data.spaceId },
    };
    // Fire-and-forget thunk: the store is updated from the callback, and the thunk returns void.
    void axiosWrapper.api.post("/spaces/removeSpace", requestData).then((response) => {
      if (!response.data.status) return;
      dispatch(deletedSpaceStatus(response.data.result));
    });
  };
}

// Delete User from a project

export async function removeUsersFromSingleProject<T extends Exact<RemoveUsersFromSingleProjectRequest, T>>(
  data: T,
): Promise<RemoveUsersFromSingleProjectResponse> {
  const response = await axiosWrapper.api.post("/projects/removeUsersFromSingleProject", data);
  return response.data;
}

export function getProjectObject(data: { id?: string }) {
  // The `id` guard lives inside the thunk so the creator always returns something
  // dispatchable. It previously returned `undefined` when `id` was absent, which
  // made `dispatch(getProjectObject(...))` throw at runtime for a missing id and
  // is untypeable under react-redux v9's precise ThunkDispatch.
  return (dispatch: AppDispatch) => {
    if (!data.id) return;
    axiosWrapper.api
      .get("/projects/getProjectObject/:id", { pathParams: { id: data.id } })
      .then((res) => {
        if (res.data.status) {
          dispatch(getProjectSettingObject(res.data.result));
        }
      })
      .catch((err: unknown) => {
        console.log("Error in project setting object ", err);
        // Reported, not re-thrown. `void Promise.reject(...)` was wrong twice over: it creates a
        // NEW rejection whose stack points here rather than at the real failure, and it trips this
        // repo's own `catch-handler/require-promise-reject` rule, which accepts captureException.
        Sentry.captureException(err);
      });
  };
}
// Each former action-dispatcher is split into dedicated endpoints. In the three helpers below `action`
// picks the endpoint and an absent one picks the list; the rest of the payload is that endpoint's own
// body. The endpoints never read `action` — request validation strips it.
const postTeamsController = postTo("/projects/teamsController");
const postInsertTeam = postTo("/projects/insertTeam");
const postUpdateTeam = postTo("/projects/updateTeam");
const postRemoveTeam = postTo("/projects/removeTeam");

export type TeamsControllerRequest =
  | ({ action: "insert" } & Parameters<typeof postInsertTeam>[0])
  | ({ action: "update" } & Parameters<typeof postUpdateTeam>[0])
  | ({ action: "delete" } & Parameters<typeof postRemoveTeam>[0])
  | ({ action?: undefined } & Parameters<typeof postTeamsController>[0]);

export const teamsController = async (data: TeamsControllerRequest) => {
  switch (data.action) {
    case "insert":
      return await postInsertTeam(data);
    case "update":
      return await postUpdateTeam(data);
    case "delete":
      return await postRemoveTeam(data);
    default:
      return await postTeamsController(data);
  }
};

const postLocationController = postTo("/projects/locationController");
const postInsertLocation = postTo("/projects/insertLocation");
const postUpdateLocation = postTo("/projects/updateLocation");
const postRemoveLocation = postTo("/projects/removeLocation");
const postReorderLocation = postTo("/projects/reorderLocation");

export type LocationControllerRequest =
  | ({ action: "insert" } & Parameters<typeof postInsertLocation>[0])
  | ({ action: "update" } & Parameters<typeof postUpdateLocation>[0])
  | ({ action: "delete" } & Parameters<typeof postRemoveLocation>[0])
  | ({ action: "reorder" } & Parameters<typeof postReorderLocation>[0])
  | ({ action?: undefined } & Parameters<typeof postLocationController>[0]);

export const locationController = async (data: LocationControllerRequest) => {
  switch (data.action) {
    case "insert":
      return await postInsertLocation(data);
    case "update":
      return await postUpdateLocation(data);
    case "delete":
      return await postRemoveLocation(data);
    case "reorder":
      return await postReorderLocation(data);
    default:
      return await postLocationController(data);
  }
};

export const projectTemplateConfigHandler = async <T extends Exact<ProjectTemplateConfigHandlerRequest, T>>(
  data: T,
): Promise<{ data: ProjectTemplateConfigHandlerResponse }> => {
  const response = await axiosWrapper.api.post("/forms/projectTemplateConfigHandler", data);
  return response;
};

const postWBSController = postTo("/projects/WBSController");
const postInsertWBS = postTo("/projects/insertWBS");
const postUpdateWBS = postTo("/projects/updateWBS");
const postRemoveWBS = postTo("/projects/removeWBS");

export type WBSControllerRequest =
  | ({ action: "insert" } & Parameters<typeof postInsertWBS>[0])
  | ({ action: "update" } & Parameters<typeof postUpdateWBS>[0])
  | ({ action: "delete" } & Parameters<typeof postRemoveWBS>[0])
  | ({ action?: undefined } & Parameters<typeof postWBSController>[0]);

export const WBSController = async (data: WBSControllerRequest) => {
  switch (data.action) {
    case "insert":
      return await postInsertWBS(data);
    case "update":
      return await postUpdateWBS(data);
    case "delete":
      return await postRemoveWBS(data);
    default:
      return await postWBSController(data);
  }
};
export const saveDefaultSpace = async <T extends Exact<SaveDefaultSpaceRequest, T>>(
  data: T,
): Promise<{ data: SaveDefaultSpaceResponse }> => {
  return await axiosWrapper.api.post("/spaces/saveDefaultSpace", data);
};
